import React, { useEffect, useRef, useState } from 'react';
import { closeIconTask } from '../../static/svgs';
import { suggestPartyByQuery, findPartyByInn, createBusinessCompany } from '../login/loginApi';
import '../tasks/task.css';
import './AddCompanyModal.css';

function debounce(fn, ms) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

const DADATA_TAX_MAP = {
  USN: 'УСН',
  ENVD: 'ЕНВД',
  ESHN: 'ЕСХН',
  SRP: 'СРП',
};

function extractPartyFields(item) {
  const data = item?.data || {};
  const fioParts = [data.fio?.surname, data.fio?.name, data.fio?.patronymic].filter(Boolean);
  const ceo = data.management?.name || (fioParts.length ? fioParts.join(' ') : '') || '';
  const emails = data.emails;
  let email = '';
  if (Array.isArray(emails) && emails.length) {
    const first = emails[0];
    email = (typeof first === 'string' ? first : first?.value) || '';
  } else if (data.email) {
    email = String(data.email);
  }
  const taxRaw = data.finance?.tax_system || data.tax_system || '';
  const tax = taxRaw ? (DADATA_TAX_MAP[String(taxRaw).toUpperCase()] || String(taxRaw)) : '';
  return {
    inn: data.inn || '',
    name: item.value || item.unrestricted_value || '',
    address: data.address?.value || '',
    ceo,
    email,
    tax,
    kpp: data.kpp || '',
    ogrn: data.ogrn || '',
    company_type: data.type === 'INDIVIDUAL' ? 'ip' : 'ooo',
  };
}

function AddCompanyModal({ businessUserId, onClose, onCreated }) {
  const [innQuery, setInnQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestSeq = useRef(0);

  const runSuggest = useRef(
    debounce(async (value) => {
      const seq = ++requestSeq.current;
      if (!value || value.length < 3) {
        setSuggestions([]);
        setSuggestLoading(false);
        return;
      }
      setSuggestLoading(true);
      try {
        const data = await suggestPartyByQuery(value);
        if (seq !== requestSeq.current) return;
        setSuggestions(data.suggestions || []);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        setSuggestions([]);
      } finally {
        if (seq === requestSeq.current) setSuggestLoading(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    if (selected) return;
    runSuggest(innQuery);
  }, [innQuery, selected, runSuggest]);

  const handleInnChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 12);
    setInnQuery(value);
    setSelected(null);
    setError('');
  };

  const handleSelectSuggestion = async (item) => {
    const base = extractPartyFields(item);
    setSelected(base);
    setInnQuery(base.inn || innQuery);
    setSuggestions([]);
    setError('');
    const inn = base.inn;
    if (!inn) return;
    try {
      const data = await findPartyByInn(inn);
      const full = data.suggestions?.[0];
      if (!full) return;
      const extra = extractPartyFields(full);
      setSelected((prev) => ({
        ...(prev || base),
        ceo: extra.ceo || prev?.ceo || '',
        email: extra.email || prev?.email || '',
        tax: extra.tax || prev?.tax || '',
        address: extra.address || prev?.address || '',
        name: extra.name || prev?.name || '',
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleAdd = async () => {
    if (!selected?.inn || !selected?.name) {
      setError('Выберите компанию из списка');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await createBusinessCompany({
        business_user_id: businessUserId,
        inn: selected.inn,
        name: selected.name,
        address: selected.address,
        kpp: selected.kpp,
        ogrn: selected.ogrn,
        company_type: selected.company_type,
        ceo: selected.ceo || '',
        email: selected.email || '',
        tax: selected.tax || '',
      });
      if (!result.success) {
        const msg = result.error || 'Не удалось добавить';
        const friendly =
          msg === 'company already added'
            ? 'Эта компания уже добавлена'
            : msg === 'inn already used'
              ? 'ИНН уже используется другой учёткой'
              : msg;
        setError(friendly);
        return;
      }
      onCreated?.(result.company);
      onClose?.();
    } catch (e) {
      console.error(e);
      setError('Ошибка добавления компании');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-container add-company-modal-overlay">
      <div className="modal-content add-company-modal">
        <div className="close-button" onClick={onClose}>
          {closeIconTask}
        </div>
        <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
          Добавить компанию
        </div>

        <div style={{ width: '100%', marginTop: '20px' }}>
          <div className="manrope-bold" style={{ fontSize: '16px', textAlign: 'left' }}>
            ИНН
          </div>
        </div>
        <div className="add-company-inn-wrap">
          <input
            className="input_task manrope-bold"
            placeholder="Введите ИНН"
            value={innQuery}
            onChange={handleInnChange}
            disabled={saving}
            autoFocus
          />
          {!selected && suggestions.length > 0 && (
            <ul className="add-company-suggest-list">
              {suggestions.map((item) => (
                <li
                  key={item.data?.hid || `${item.data?.inn}-${item.value}`}
                  className="add-company-suggest-item"
                  onClick={() => handleSelectSuggestion(item)}
                >
                  <div className="manrope-bold add-company-suggest-name">{item.value}</div>
                  <div className="add-company-suggest-meta">ИНН: {item.data?.inn}</div>
                  {item.data?.address?.value ? (
                    <div className="add-company-suggest-meta">{item.data.address.value}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {!selected && suggestLoading && (
            <div className="add-company-hint manrope-bold">Поиск…</div>
          )}
        </div>

        {selected && (
          <div className="add-company-preview">
            <div className="add-company-preview-row">
              <div className="add-company-preview-label manrope-bold">Название</div>
              <div className="add-company-preview-value manrope-bold">{selected.name}</div>
            </div>
            <div className="add-company-preview-row">
              <div className="add-company-preview-label manrope-bold">Адрес</div>
              <div className="add-company-preview-value manrope-bold">
                {selected.address || '—'}
              </div>
            </div>
            <div className="add-company-preview-row">
              <div className="add-company-preview-label manrope-bold">
                {selected.company_type === 'ip' ? 'Предприниматель' : 'Директор'}
              </div>
              <div className="add-company-preview-value manrope-bold">
                {selected.ceo || '—'}
              </div>
            </div>
          </div>
        )}

        {error ? <div className="add-company-error manrope-bold">{error}</div> : null}

        <div className="add-company-actions">
          <button
            type="button"
            className="profile-access-btn profile-access-btn--cancel manrope-bold"
            onClick={onClose}
            disabled={saving}
          >
            Отмена
          </button>
          <button
            type="button"
            className="profile-access-btn profile-access-btn--save manrope-bold"
            onClick={handleAdd}
            disabled={saving || !selected}
          >
            {saving ? 'Добавление…' : 'Добавить'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddCompanyModal;
